import { useEffect, useMemo, useState } from "react";
import { fetchRecordings, recordingMediaUrl } from "./api.js";
import { shortId } from "./format.js";

const PAGE = 40;

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
}

function dayBucket(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { key: 0, label: "Unknown date" };
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const diff = Math.round((todayStart - start) / 86400000);
  if (diff === 0) return { key: start, label: `Today · ${date}` };
  if (diff === 1) return { key: start, label: `Yesterday · ${date}` };
  return { key: start, label: date };
}

function kindLabel(kind) {
  if (kind === "audio") return "Audio";
  if (kind === "screen") return "Screen";
  if (kind === "video") return "Video";
  return kind || "Media";
}

function matchesType(kind, type) {
  if (type === "all") return true;
  if (type === "audio") return kind === "audio";
  return kind === "video" || kind === "screen";
}

function isEmptyFile(row) {
  return !!row.endedAt && !row.playable;
}

function formatBytes(n) {
  const bytes = Number(n);
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const text = value >= 10 || i === 0 ? value.toFixed(0) : value.toFixed(1);
  return `${text} ${units[i]}`;
}

export function RecordingsPanel({ apiBase, onCount }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [playId, setPlayId] = useState("");
  const [q, setQ] = useState("");
  const [roomId, setRoomId] = useState("");
  const [type, setType] = useState("all");
  const [hideEmpty, setHideEmpty] = useState(true);
  const [limit, setLimit] = useState(PAGE);
  const [closed, setClosed] = useState({});
  const [enabled, setEnabled] = useState(false);
  const [bytes, setBytes] = useState(0);
  const [files, setFiles] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetchRecordings(apiBase)
        .then((data) => {
          if (cancelled) return;
          setRows(data.recordings);
          setEnabled(data.enabled === true);
          setBytes(data.bytes);
          setFiles(data.files);
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof Error ? err.message : "Could not load recordings");
        });
    load();
    const timer = setInterval(load, 8000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [apiBase]);

  useEffect(() => {
    onCount?.(Array.isArray(rows) ? rows.length : 0);
  }, [rows, onCount]);

  const rooms = useMemo(() => {
    const map = new Map();
    for (const row of rows || []) {
      if (!row.roomId || map.has(row.roomId)) continue;
      map.set(row.roomId, row.roomName || shortId(row.roomId));
    }
    return [...map.entries()];
  }, [rows]);

  const matched = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (rows || []).filter((row) => {
      if (roomId && row.roomId !== roomId) return false;
      if (!matchesType(row.kind, type)) return false;
      if (!query) return true;
      const blob = `${row.roomName || ""} ${row.roomId || ""} ${row.publisherUserid || ""}`.toLowerCase();
      return blob.includes(query);
    });
  }, [rows, q, roomId, type]);

  const emptyCount = matched.filter(isEmptyFile).length;
  const pool = hideEmpty ? matched.filter((row) => !isEmptyFile(row)) : matched;
  const shown = pool.slice(0, limit);
  const playing = (rows || []).find((row) => row.id === playId) || null;

  const groups = [];
  for (const row of shown) {
    const bucket = dayBucket(row.startedAt);
    let group = groups.find((g) => g.key === bucket.key);
    if (!group) {
      group = { key: bucket.key, label: bucket.label, rows: [] };
      groups.push(group);
    }
    group.rows.push(row);
  }

  return (
    <div>
      {error ? <div className="bm-err">{error}</div> : null}
      <div className="bm-rec-status">
        <span className={enabled ? "on" : "off"}>{enabled ? "Recording on" : "Recording off"}</span>
        <span>{formatBytes(bytes)} on disk · {files} file{files === 1 ? "" : "s"}</span>
      </div>
      <div className="bm-rec-tools">
        <div className="bm-search bm-rec-search">
          <span className="bm-glyph" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} placeholder="Room or publisher" />
        </div>
        <select className="bm-host-select" value={roomId} onChange={(e) => { setRoomId(e.target.value); setLimit(PAGE); }}>
          <option value="">All rooms</option>
          {rooms.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
        <div className="bm-seg" role="tablist">
          {[
            ["all", "All"],
            ["video", "Video"],
            ["audio", "Audio"],
          ].map(([id, label]) => (
            <button key={id} type="button" className={type === id ? "on" : ""} onClick={() => { setType(id); setLimit(PAGE); }}>
              {label}
            </button>
          ))}
        </div>
        <label className="bm-check">
          <input type="checkbox" checked={hideEmpty} onChange={(e) => { setHideEmpty(e.target.checked); setLimit(PAGE); }} />
          Hide empty ({emptyCount})
        </label>
      </div>
      {!rows ? <div className="bm-empty">Loading…</div> : null}
      {rows && pool.length === 0 ? <div className="bm-empty">No recordings match.</div> : null}
      {groups.length > 0 ? (
        <div className="bm-rec-list">
          {groups.map((group) => {
            const shut = !!closed[group.key];
            return (
              <section key={group.key}>
                <button
                  type="button"
                  className="bm-rec-day"
                  aria-expanded={!shut}
                  onClick={() => setClosed((s) => ({ ...s, [group.key]: !s[group.key] }))}
                >
                  {shut ? "▸" : "▾"} {group.label} — {group.rows.length} recording{group.rows.length === 1 ? "" : "s"}
                </button>
                {shut ? null : group.rows.map((row) => (
                  <div key={row.id}>
                    <div className="bm-rec-row">
                      <div className="bm-rec-glyph" aria-hidden="true">{kindLabel(row.kind).slice(0, 1)}</div>
                      <div>
                        <div>{when(row.startedAt) || "—"} · {kindLabel(row.kind)}</div>
                        <div className="bm-rec-meta">{row.roomName || shortId(row.roomId)}</div>
                      </div>
                      <div className="bm-rec-pub" title={row.publisherUserid}>{shortId(row.publisherUserid)}</div>
                      {row.playable ? (
                        <button type="button" className="bm-rec-play" onClick={() => setPlayId(row.id === playId ? "" : row.id)}>
                          {row.id === playId ? "Close" : "Play"}
                        </button>
                      ) : (
                        <span className="bm-rec-empty">{row.endedAt ? "empty" : "recording"}</span>
                      )}
                    </div>
                    {playing && playing.id === row.id ? (
                      <div className="bm-rec-player">
                        {playing.kind === "audio" ? (
                          <audio controls autoPlay src={recordingMediaUrl(apiBase, playing.id)} />
                        ) : (
                          <video controls autoPlay playsInline src={recordingMediaUrl(apiBase, playing.id)} />
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
              </section>
            );
          })}
        </div>
      ) : null}
      {rows ? (
        <div className="bm-rec-foot">
          <span>showing {shown.length} of {pool.length}</span>
          {shown.length < pool.length ? (
            <button type="button" className="bm-host-link" onClick={() => setLimit((n) => n + PAGE)}>Load older</button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
